import { RefObject, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useTableHorizontalScroll } from '../hooks/useTableHorizontalScroll';
import { tableScrollAffordancesSx } from '../styles';

type Props = {
  tableAreaRef: RefObject<HTMLElement>;
  rerenderKey: unknown;
};

// The measuring lives here, not in the table's parent, so a scroll re-renders
// only these two small elements and never the table.
export const TableScrollAffordances: React.FC<Props> = ({
  tableAreaRef,
  rerenderKey
}) => {
  const scroll = useTableHorizontalScroll(tableAreaRef, rerenderKey);
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const { scroller } = scroll;

  useEffect(() => {
    const stickyBar = stickyBarRef.current;
    if (!scroller || !stickyBar) return;

    // Each side follows the other only when they are a pixel or more apart.
    // Otherwise every move echoes back and the two fight, which judders on a
    // trackpad. A fractional table position never equals the bar's whole one.
    const isApart = () =>
      Math.abs(stickyBar.scrollLeft - scroller.scrollLeft) >= 1;
    const followTable = () => {
      if (isApart()) stickyBar.scrollLeft = scroller.scrollLeft;
    };
    const followStickyBar = () => {
      if (isApart()) scroller.scrollLeft = stickyBar.scrollLeft;
    };

    followTable();
    scroller.addEventListener('scroll', followTable);
    stickyBar.addEventListener('scroll', followStickyBar);

    return () => {
      scroller.removeEventListener('scroll', followTable);
      stickyBar.removeEventListener('scroll', followStickyBar);
    };
  }, [scroller, scroll.isScrollbarOffScreen]);

  return (
    <>
      <Box
        data-testid="table-scroll-fade"
        aria-hidden
        sx={tableScrollAffordancesSx.fade(scroll)}
      />
      {scroll.isScrollbarOffScreen && (
        <Box
          ref={stickyBarRef}
          data-testid="table-sticky-scrollbar"
          sx={tableScrollAffordancesSx.stickyScrollbar(scroll)}
        >
          <Box sx={tableScrollAffordancesSx.stickyScrollbarTrack(scroll)} />
        </Box>
      )}
    </>
  );
};
